import {
  Box,
  Clapperboard,
  FileText,
  FolderKanban,
  Image,
  LayoutGrid,
  MapPin,
  MessageSquareText,
  MoreHorizontal,
  Package,
  Pencil,
  Pin,
  PinOff,
  Sparkles,
  Users,
  Video,
} from "lucide-react"
import { useEffect, useRef, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { AgentWorkingDots } from "@/components/agent/agent-working-dots"
import { TabDirtyDot } from "@/components/ui/tab-dirty-dot"
import { EntityThumbSquare } from "@/components/workspace/entity-thumb-square"
import type {
  AgentActivityProcess,
  AgentActivityProcessKind,
  AgentActivityProcessState,
  AgentActivityRow,
} from "@/lib/agent-activity-api"
import { cn } from "@/lib/utils"

/** Fields the row card actually renders. Activity rows and monitoring dialogs both satisfy this. */
export type AgentActivityCardRow = {
  mode: string
  entity_kind: string
  title: string
  thumb_url: string | null
  last_message_preview: string | null
  last_message_role: string | null
  status_bucket: AgentActivityRow["status_bucket"]
  processes?: AgentActivityProcess[]
  reply_state?: AgentActivityProcessState | null
}

type AgentActivityRowCardProps<T extends AgentActivityCardRow> = {
  row: T
  theme?: "dark" | "light"
  selected?: boolean
  pinned?: boolean
  pinDisabled?: boolean
  /** Activity queue replaces surface titles with i18n. Callers that already pass a title keep it. */
  keepTitle?: boolean
  onOpen: (row: T) => void
  onPinToggle?: (row: T, pinned: boolean) => void
  /** Pointer drag after a few pixels. Click is swallowed once the gesture starts. */
  dragEnabled?: boolean
  dragging?: boolean
  onDragGesture?: (point: { clientX: number; clientY: number }) => boolean | void
}

const DRAG_THRESHOLD_PX = 4

const SURFACE_MODES = [
  "shots_assistant",
  "script_assistant",
  "scene3d_assistant",
  "skills_discussion_assistant",
  "skills_editor_assistant",
  "projects_assistant",
] as const

type SurfaceMode = (typeof SURFACE_MODES)[number]

function isSurfaceMode(mode: string): mode is SurfaceMode {
  return (SURFACE_MODES as readonly string[]).includes(mode)
}

function cardChrome(selected: boolean): string {
  // Selected: same tokens as workspace nav; default: no raised pad / hover.
  if (selected) {
    return "rounded-lg bg-[var(--ui-nav-active-bg)] text-[var(--ui-nav-active-fg)]"
  }
  return "rounded-lg text-ui-text-primary"
}

function processIconTone(state: "running" | "done" | "idle"): string {
  if (state === "done") return "text-emerald-500"
  return "text-ui-text-muted"
}

function AssetProcessIcon({ kind, className }: { kind: string; className: string }) {
  switch (kind as AgentActivityProcessKind) {
    case "image":
      return <Image className={className} />
    case "video":
      return <Video className={className} />
    case "batch":
      return <MoreHorizontal className={className} />
    default:
      return <MoreHorizontal className={className} />
  }
}

function entityFallbackIcon(row: AgentActivityCardRow): ReactNode {
  const cls = "h-3.5 w-3.5 text-ui-text-muted"
  if (row.entity_kind === "project_surface" || row.entity_kind === "company_surface") {
    switch (row.mode) {
      case "shots_assistant":
        return <Clapperboard className={cls} />
      case "script_assistant":
        return <FileText className={cls} />
      case "scene3d_assistant":
        return <Box className={cls} />
      case "skills_discussion_assistant":
        return <Sparkles className={cls} />
      case "skills_editor_assistant":
        return <Pencil className={cls} />
      case "projects_assistant":
        return <FolderKanban className={cls} />
      default:
        return <Sparkles className={cls} />
    }
  }
  switch (row.entity_kind) {
    case "character":
      return <Users className={cls} />
    case "location":
      return <MapPin className={cls} />
    case "prop":
      return <Package className={cls} />
    case "shot":
      return <Clapperboard className={cls} />
    case "scene3d":
      return <Box className={cls} />
    case "canvas":
      return <LayoutGrid className={cls} />
    default:
      return <Sparkles className={cls} />
  }
}

export function AgentActivityRowCard<T extends AgentActivityCardRow>({
  row,
  theme: _theme,
  selected = false,
  pinned = false,
  pinDisabled = false,
  keepTitle = false,
  onOpen,
  onPinToggle,
  dragEnabled = false,
  dragging = false,
  onDragGesture,
}: AgentActivityRowCardProps<T>) {
  const { t } = useTranslation("agentActivity")
  const suppressClickRef = useRef(false)
  const dragCleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => dragCleanupRef.current?.()
  }, [])
  const surfaceTitle =
    !keepTitle &&
    (row.entity_kind === "project_surface" || row.entity_kind === "company_surface") &&
    isSurfaceMode(row.mode)
      ? t(`surfaces.${row.mode}`)
      : null
  const displayTitle = surfaceTitle ?? row.title

  const previewPrefix =
    row.last_message_role === "assistant"
      ? `${t("preview.agent")}: `
      : row.last_message_role === "user"
        ? `${t("preview.you")}: `
        : ""
  const hasPreview = Boolean(row.last_message_preview)
  const unread = row.status_bucket === "ready_unread"
  const inProgress = row.status_bucket === "in_progress"
  const processes = row.processes ?? []
  const replyState = row.reply_state ?? null

  const handlePinClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    onPinToggle?.(row, !pinned)
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragEnabled || !onDragGesture || event.button !== 0) return
    const startX = event.clientX
    const startY = event.clientY
    const pointerId = event.pointerId
    const cleanup = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
      dragCleanupRef.current = null
    }
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < DRAG_THRESHOLD_PX) return
      cleanup()
      suppressClickRef.current = true
      if (onDragGesture({ clientX: ev.clientX, clientY: ev.clientY }) === false) {
        suppressClickRef.current = false
      }
    }
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      cleanup()
    }
    dragCleanupRef.current?.()
    dragCleanupRef.current = cleanup
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
  }

  return (
    <div
      className={cn("group relative", cardChrome(selected), dragging && "opacity-40")}
      onPointerDown={dragEnabled ? handlePointerDown : undefined}
    >
      <button
        type="button"
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false
            return
          }
          onOpen(row)
        }}
        className="block w-full p-1.5 text-left text-xs transition-colors"
      >
        <div className="flex items-start gap-2">
          <EntityThumbSquare
            name={displayTitle}
            imageUrl={inProgress ? null : row.thumb_url}
            size="radio"
            fallbackIcon={inProgress ? <AgentWorkingDots className="text-ui-text-muted" /> : entityFallbackIcon(row)}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <p
                  className={cn(
                    "min-w-0 truncate font-medium",
                    selected ? "text-[var(--ui-nav-active-fg)]" : "text-ui-text-primary",
                  )}
                >
                  {displayTitle}
                </p>
                {!inProgress && unread ? (
                  <TabDirtyDot tone="approved" className="relative top-auto right-auto shrink-0" />
                ) : null}
              </div>
              {replyState || processes.length > 0 ? (
                <div className="flex shrink-0 items-center gap-1">
                  {replyState ? (
                    <MessageSquareText className={cn("h-3 w-3 shrink-0", processIconTone(replyState))} />
                  ) : null}
                  {processes.map((proc) => (
                    <AssetProcessIcon
                      key={proc.kind}
                      kind={proc.kind}
                      className={cn("h-3 w-3 shrink-0", processIconTone(proc.state))}
                    />
                  ))}
                </div>
              ) : null}
            </div>
            <p className="mt-0.5 line-clamp-1 pr-7 text-[11px] text-ui-text-secondary">
              {hasPreview ? (
                <>
                  {previewPrefix}
                  {row.last_message_preview}
                </>
              ) : (
                t("preview.empty")
              )}
            </p>
          </div>
        </div>
      </button>
      {onPinToggle ? (
        <button
          type="button"
          aria-label={pinned ? t("unpin") : t("pin")}
          title={pinned ? t("unpin") : t("pin")}
          disabled={pinDisabled}
          className={cn(
            "group/pin absolute right-1.5 bottom-1.5 z-10 flex h-5 w-5 items-center justify-end text-ui-text-muted transition-[opacity,colors] hover:text-ui-text-primary focus-visible:opacity-100 disabled:pointer-events-none",
            pinned ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          )}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={handlePinClick}
        >
          {pinned ? (
            <>
              <Pin
                className="h-3 w-3 group-hover/pin:hidden group-focus-visible/pin:hidden"
                strokeWidth={1.75}
                aria-hidden
              />
              <PinOff
                className="hidden h-3 w-3 group-hover/pin:block group-focus-visible/pin:block"
                strokeWidth={1.75}
                aria-hidden
              />
            </>
          ) : (
            <Pin className="h-3 w-3" strokeWidth={1.75} aria-hidden />
          )}
        </button>
      ) : null}
    </div>
  )
}
